/**
 * Paper.js / toolkit helpers for listing project ROI features and zooming to them.
 * Shared by BoundingBox TopBarControls (review, zoom) and ProjectRoiNavigator.
 */

import { getBoundingBoxCategoryFromPaperItem } from '../../../utils/annotationUserdata';

/** True when the toolkit has a live Paper.js project (safe to call getFeatureCollectionGroups). */
export function toolkitHasLiveProject(tk) {
    try {
        return Boolean(tk?.paperScope?.project);
    } catch {
        return false;
    }
}

/** Return feature items for a category, sorted in stable review order (top, left, tie-breaker). */
export function getFeatureItemsByCategory(tk, categoryId) {
    if (!tk || !categoryId || !toolkitHasLiveProject(tk)) return [];
    let groups;
    try {
        groups = tk.getFeatureCollectionGroups?.();
    } catch {
        return [];
    }
    if (!Array.isArray(groups)) return [];
    const all = [];
    groups.forEach((group) => {
        if (!group || typeof group.getItems !== 'function') return;
        const items = group.getItems({ match: (i) => i.isGeoJSONFeature }) ?? [];
        items.forEach((item) => {
            const gt = item.annotationItem?.getGeoJSONType?.();
            if (!gt || gt.type === null) return;
            const itemCategoryId = getBoundingBoxCategoryFromPaperItem(item).categoryId;
            if (itemCategoryId !== categoryId) return;
            if (!item.parent) return;
            all.push(item);
        });
    });
    return all
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) => {
            const ab = a.item.bounds;
            const bb = b.item.bounds;
            const byTop = (ab?.top ?? 0) - (bb?.top ?? 0);
            if (byTop !== 0) return byTop;
            const byLeft = (ab?.left ?? 0) - (bb?.left ?? 0);
            if (byLeft !== 0) return byLeft;
            const aKey = a.item.data?.userdata?.dsa?.elementId ?? a.item.id ?? '';
            const bKey = b.item.data?.userdata?.dsa?.elementId ?? b.item.id ?? '';
            const byKey = String(aKey).localeCompare(String(bKey));
            if (byKey !== 0) return byKey;
            return a.originalIndex - b.originalIndex;
        })
        .map((x) => x.item);
}

/** Collect ROI-labeled features in roiConfig.categoryIds order (deduped by stable id). */
export function getAllRoiFeatureItems(tk, roiCfg) {
    if (!tk || !toolkitHasLiveProject(tk) || !roiCfg?.enabled || !roiCfg.categoryIds?.length) return [];
    const seen = new Set();
    const out = [];
    for (const cid of roiCfg.categoryIds) {
        const items = getFeatureItemsByCategory(tk, cid);
        for (const item of items) {
            const key = getReviewItemElementId(item) || item?.id || String(out.length);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(item);
        }
    }
    return out;
}

/** Stable id for resume/jump (matches sort tie-breaker in getFeatureItemsByCategory). */
export function getReviewItemElementId(item) {
    if (!item) return '';
    return String(item.data?.userdata?.dsa?.elementId ?? item.id ?? '');
}

/** Fit viewer viewport to an item bounds with configurable margin. */
export function zoomToItem(tk, item, opts = {}) {
    const margin = opts.margin ?? 0.2;
    const viewer = tk?.overlay?.viewer;
    const viewport = viewer?.viewport;
    const b = item?.bounds;
    if (!viewport || !b) return;
    const width = Math.max(b.width ?? 0, 1);
    const height = Math.max(b.height ?? 0, 1);
    const padX = width * margin * 0.5;
    const padY = height * margin * 0.5;
    const x = b.left - padX;
    const y = b.top - padY;
    const w = width + padX * 2;
    const h = height + padY * 2;

    if (typeof viewport.imageToViewportRectangle === 'function') {
        const rect = viewport.imageToViewportRectangle(x, y, w, h);
        if (typeof viewport.fitBoundsWithConstraints === 'function') {
            viewport.fitBoundsWithConstraints(rect);
            return;
        }
        if (typeof viewport.fitBounds === 'function') {
            viewport.fitBounds(rect);
            return;
        }
    }
    if (typeof viewport.fitBounds === 'function') {
        viewport.fitBounds({ x, y, width: w, height: h });
    }
}
